import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import toast from "react-hot-toast";

function userName(u) {
  if (!u) return "—";
  return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "—";
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function TlLeaveList() {
  const navigate = useNavigate();

  const [rows, setRows]   = useState([]);
  const [meta, setMeta]   = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage]   = useState(1);
  const [limit, setLimit] = useState(10);
  const [deleteId, setDeleteId] = useState(null);

  const fetchLeaves = async (opts = {}) => {
    const nextPage  = opts.page  ?? page;
    const nextLimit = opts.limit ?? limit;
    setLoading(true);
    try {
      const res = await api.get("/admin/tl-leave-schedules", {
        params: { page: nextPage, limit: nextLimit },
      });
      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, totalPages: 1 });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load leave records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeaves({ page: 1 }); }, []);

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchLeaves({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchLeaves({ page: p });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 10;
    setLimit(v);
    setPage(1);
    fetchLeaves({ page: 1, limit: v });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this leave record?")) return;
    setDeleteId(id);
    try {
      await api.delete(`/admin/tl-leave-schedules/${id}`);
      toast.success("Leave record removed");
      fetchLeaves({ page });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove leave");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">TL Leave Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage Team Lead leave schedules and escalation handovers.
          </p>
        </div>
        <button
          type="button"
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 self-start sm:self-auto"
          onClick={() => navigate("/admin/tl-leave-management/create")}
        >
          + Add Leave
        </button>
      </div>

      {/* Table */}
      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Leave Records ({meta.total})</span>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <select
              value={limit}
              onChange={onLimitChange}
              className="border rounded-lg px-2 py-1 text-xs focus:outline-none"
            >
              {[10, 25, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button
              type="button"
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-gray-600 text-xs">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b>
            </span>
            <button
              type="button"
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No leave records found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Team Lead</th>
                <th className="px-4 py-3 whitespace-nowrap">Leave Period</th>
                <th className="px-4 py-3">Handover</th>
                <th className="px-4 py-3">Assigned TL</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="border-t">
              {rows.map((leave) => (
                <tr key={leave.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{userName(leave.tl_user)}</div>
                    <div className="text-xs text-gray-400">{leave.tl_user?.email || "—"}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs">
                    <div>{formatDate(leave.leave_start_date)}</div>
                    <div className="text-gray-400">to {formatDate(leave.leave_end_date)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                      leave.handover_type === "manual"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-green-100 text-green-700"
                    }`}>
                      {leave.handover_type === "manual" ? "Manual" : "Auto"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {leave.handover_type === "manual"
                      ? userName(leave.assigned_tl)
                      : <span className="text-gray-400 italic">System assigned</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 max-w-[180px] truncate">
                    {leave.notes || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/tl-leave-management/edit/${leave.id}`)}
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={deleteId === leave.id}
                        onClick={() => handleDelete(leave.id)}
                        className="rounded-lg border border-red-200 text-red-600 px-3 py-1 text-xs font-semibold hover:bg-red-50 disabled:opacity-40"
                      >
                        {deleteId === leave.id ? "…" : "Remove"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
